import { array } from '@codegrade/utils';
import { getStoreBuilder } from '@codegrade/vuex-typex';
import client from '@/client';
import * as utils from '@/utils';
const storeBuilder = getStoreBuilder();
/** @returns Fresh initial state. */
function makeInitialState() {
    return { byTenant: {} };
}
const moduleBuilder = storeBuilder.module('tenantRoles', makeInitialState());
/** The Tenant Roles store module. */
export var TenantRolesStore;
(function (TenantRolesStore) {
    const commitData = moduleBuilder.commit((state, { tenantId, data, }) => {
        utils.vueSet(state.byTenant, tenantId, data);
    }, 'commitData');
    const commitClear = moduleBuilder.commit(state => {
        Object.assign(state, makeInitialState());
    }, 'commitClear');
    /**
     * Get the roles for a tenant.
     *
     * @returns A function that takes a tenant ID and returns the paginated loading state.
     */
    TenantRolesStore.getRoles = moduleBuilder.read(state => 
    /**
     * @param tenantId The tenant to get roles for.
     * @returns The paginated loading state for this tenant's roles.
     */
    (tenantId) => utils.getPaginatedState(state.byTenant[tenantId]), 'getRoles');
    /**
     * Load roles for a tenant.
     */
    TenantRolesStore.loadRoles = moduleBuilder.dispatch(async ({ state }, { tenantId, toLoad, }) => {
        await utils.loadPaginatedWithForce({
            getData: () => state.byTenant[tenantId],
            commitData: data => commitData({ tenantId, data }),
            load: async (nextToken) => await client.p.tenant.getRoles({
                tenantId,
                nextToken,
            }),
            toLoad,
        });
    }, 'loadRoles');
    /** Update a role's cached permission value (no API call). */
    TenantRolesStore.commitRolePermission = moduleBuilder.commit((state, { tenantId, roleId, permission, value, }) => {
        const existing = state.byTenant[tenantId];
        if (existing?.data == null) {
            return;
        }
        const idx = array.findMaybeIndex(r => r.id === roleId, existing.data).extract();
        if (idx == null) {
            return;
        }
        const role = existing.data[idx];
        const updated = [...existing.data];
        updated[idx] = {
            ...role,
            perms: { ...role.perms, [permission]: value },
        };
        utils.vueSet(state.byTenant, tenantId, { ...existing, data: updated });
    }, 'commitRolePermission');
    /**
     * Patch a role's permission within a tenant and update the store.
     */
    TenantRolesStore.patchRole = moduleBuilder.dispatch(async (_ctx, { tenantId, roleId, data, }) => {
        const result = await client.p.tenant.patchRole({
            tenantId,
            roleId,
            requestBody: data,
        });
        TenantRolesStore.commitRolePermission({
            tenantId,
            roleId,
            permission: data.permission,
            value: data.value,
        });
        return result;
    }, 'patchRole');
    utils.signal.AFTER_USER_CHANGED.registerSync(() => {
        commitClear();
    });
})(TenantRolesStore || (TenantRolesStore = {}));
